import React from 'react';
import { Button, Card, Flex, Form, Input, InputNumber, Select, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  CHANNEL_RULE_KEYS,
  CHANNEL_RULE_REPEAT_MAX_DAYS,
  CHANNEL_RULE_REPEAT_MIN_DAYS,
  CHANNEL_RULE_SUFFIX_MAX_LENGTH,
  ChannelRuleEntity,
  ChannelRuleKey,
  defaultChannelRule,
  nextAvailableChannelRuleKey,
} from '../../../domain/entities/ChannelRule';

const RULES_FIELD = 'rules';

const ChannelRulesField: React.FC = () => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const rules: ChannelRuleEntity[] = Form.useWatch(RULES_FIELD, form) ?? [];
  const nextKey = nextAvailableChannelRuleKey(rules);

  const changeKey = (index: number, key: ChannelRuleKey) => {
    const updated = [...rules];
    updated[index] = defaultChannelRule(key);
    form.setFieldValue(RULES_FIELD, updated);
  };

  const keyOptions = (current?: ChannelRuleKey) => CHANNEL_RULE_KEYS.map((key) => ({
    value: key,
    label: t(`channel-rule-${key}`, { ns: 'labels' }),
    disabled: key !== current && rules.some((rule) => rule?.key === key),
  }));

  const renderParams = (name: number, key?: ChannelRuleKey) => {
    if (key === 'advert_suffix') {
      return (
        <Form.Item
          name={[name, 'text']}
          extra={t('channel-rule-advert_suffix-hint', { ns: 'labels' })}
          rules={[{ required: true, whitespace: true, message: t('channel-rule-suffix-invalid', { ns: 'errors' }) }]}
          style={{ marginBottom: 0 }}
        >
          <Input.TextArea
            autoSize={{ minRows: 1, maxRows: 4 }}
            maxLength={CHANNEL_RULE_SUFFIX_MAX_LENGTH}
            showCount
            placeholder={t('channel-rule-advert_suffix-placeholder', { ns: 'labels' })}
          />
        </Form.Item>
      );
    }
    if (key === 'advert_repeat_interval') {
      return (
        <Form.Item
          name={[name, 'repeatIntervalDays']}
          extra={t('channel-rule-advert_repeat_interval-hint', { ns: 'labels', max: CHANNEL_RULE_REPEAT_MAX_DAYS })}
          rules={[{
            required: true,
            type: 'integer',
            min: CHANNEL_RULE_REPEAT_MIN_DAYS,
            max: CHANNEL_RULE_REPEAT_MAX_DAYS,
            message: t('channel-rule-repeat-interval-invalid', { ns: 'errors' }),
          }]}
          style={{ marginBottom: 0 }}
        >
          <InputNumber
            min={CHANNEL_RULE_REPEAT_MIN_DAYS}
            max={CHANNEL_RULE_REPEAT_MAX_DAYS}
            precision={0}
            addonAfter={t('channel-rule-days', { ns: 'labels' })}
            style={{ width: '100%' }}
          />
        </Form.Item>
      );
    }
    return null;
  };

  return (
    <Form.Item label={t('channel-rules', { ns: 'labels' })} extra={t('channel-rules-hint', { ns: 'labels' })}>
      <Form.List name={RULES_FIELD}>
        {(fields, { add, remove }) => (
          <Flex vertical gap={8}>
            {fields.length === 0 && (
              <Typography.Text type="secondary">{t('channel-rules-empty', { ns: 'labels' })}</Typography.Text>
            )}
            {fields.map(({ key, name }) => {
              const ruleKey = rules[name]?.key;
              return (
                <Card key={key} size="small">
                  <Flex vertical gap={8}>
                    <Flex gap={8} align="flex-start">
                      <Form.Item name={[name, 'key']} style={{ flex: 1, marginBottom: 0 }}>
                        <Select options={keyOptions(ruleKey)} onChange={(value: ChannelRuleKey) => changeKey(name, value)} />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label={t('channel-rule-remove', { ns: 'buttons' })}
                        onClick={() => remove(name)}
                      />
                    </Flex>
                    {renderParams(name, ruleKey)}
                  </Flex>
                </Card>
              );
            })}
            <Button type="dashed" icon={<PlusOutlined />} disabled={!nextKey} onClick={() => nextKey && add(defaultChannelRule(nextKey))}>
              {t('channel-rule-add', { ns: 'buttons' })}
            </Button>
          </Flex>
        )}
      </Form.List>
    </Form.Item>
  );
};

export default ChannelRulesField;
